import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Legend, ReferenceLine,
} from "recharts";
import { api } from "../lib/api";
import { fmtBRL } from "../lib/format";
import { computeDelta } from "../lib/delta";
import { useFilters, periodToDates } from "../lib/filters";
import { KPIStatCard } from "../components/KPIStatCard";
import { ChartCard } from "../components/ChartCard";
import { PageHeader } from "../components/PageHeader";
import { MemoryLink } from "../components/MemoryLink";
import { IconAlertTriangle, IconInfo } from "../components/icons";
import type { TransportCashflowDto } from "@bi/types";

/** Janela [dia, dia+1) em ISO para drill de um dia do gráfico. */
function dayWindow(ymd: string): { from: string; to: string } {
  const from = new Date(`${ymd}T00:00:00.000Z`);
  const to = new Date(from.getTime() + 86400000);
  return { from: from.toISOString(), to: to.toISOString() };
}

const MONTH_PT_SHORT = ["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"];

function monthLabel(ym: string): string {
  const [, m] = ym.split("-");
  return MONTH_PT_SHORT[parseInt(m ?? "1", 10) - 1] ?? ym;
}

function dayLabel(iso: string): string {
  const [, m, d] = iso.split("-");
  return `${d}/${m}`;
}

function axisK(v: number): string {
  if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 1_000)     return `${(v / 1_000).toFixed(0)}k`;
  return String(v);
}

const ALERT_STYLE: Record<string, string> = {
  critical: "bg-danger-50 border-danger-200 text-danger-700",
  warning:  "bg-amber-50 border-amber-200 text-amber-700",
  info:     "bg-info-50 border-info-100 text-info-700",
};

export function CashflowPage() {
  const navigate = useNavigate();
  const { filters } = useFilters();
  const { from, to } = periodToDates(filters.period, filters.customFrom, filters.customTo);
  // Dia selecionado nos gráficos (drill "títulos do dia")
  const [projDay, setProjDay] = useState<string | null>(null);
  const [realDay, setRealDay] = useState<string | null>(null);

  const { data: cf, isLoading } = useQuery({
    queryKey: ["transport", "cashflow", from, to, filters.comparison],
    queryFn: () =>
      api.get<TransportCashflowDto>(
        `/dashboards/transport/cashflow?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&comparison=${filters.comparison}`,
      ).catch(() => null),
    staleTime: 60_000,
  });

  const inflowDelta  = computeDelta(cf?.inflows,  cf?.inflowsPrev,  { direction: "higher_is_better" });
  const outflowDelta = computeDelta(cf?.outflows, cf?.outflowsPrev, { direction: "higher_is_worse" });
  const netDelta     = computeDelta(cf?.netPeriod, cf?.netPrev,     { direction: "higher_is_better" });

  // Série diária para o gráfico combinado (rawDate = YYYY-MM-DD p/ drill do dia)
  const dailyData = (cf?.daily ?? []).map((d) => ({
    date:    dayLabel(d.date),
    rawDate: d.date,
    Entradas: d.inflow,
    Saídas:   d.outflow,
    Saldo:    d.net,
  }));

  // Projeção futura (tabela)
  const nowYM = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  })();
  const futureRows = (cf?.projection ?? []).filter((p) => p.period >= nowYM);

  const alerts = cf?.alerts ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Fluxo de Caixa"
        subtitle="Entradas, saídas, saldo e risco — realizado no período + projeção"
      />

      {/* ── Alertas ─────────────────────────────────────────────────────────── */}
      {!isLoading && alerts.length > 0 && (
        <div className="space-y-2">
          {alerts.map((a, i) => (
            <div key={i} className={`flex items-start gap-3 px-4 py-3 rounded-xl border text-sm ${ALERT_STYLE[a.severity] ?? ALERT_STYLE.info}`}>
              <IconAlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{a.message}</span>
            </div>
          ))}
        </div>
      )}

      {/* ── KPIs do período ──────────────────────────────────────────────────── */}
      <div>
        <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest mb-3">Realizado no período</p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <MemoryLink cardKey="recebido-no-periodo" periodFrom={from} periodTo={to}>
            <KPIStatCard
              label="Entradas no período"
              value={cf ? fmtBRL(cf.inflows) : "—"}
              delta={inflowDelta}
              comparisonLabel="recebimentos"
              status="good"
              loading={isLoading}
            />
          </MemoryLink>
          <MemoryLink cardKey="pago-no-periodo" periodFrom={from} periodTo={to}>
            <KPIStatCard
              label="Saídas no período"
              value={cf ? fmtBRL(cf.outflows) : "—"}
              delta={outflowDelta}
              comparisonLabel="pagamentos ⚠️"
              status="neutral"
              loading={isLoading}
            />
          </MemoryLink>
          <div className="cursor-default" title="Valor derivado: Entradas no período − Saídas no período (clique nos dois cards ao lado para ver a composição)">
            <KPIStatCard
              label="Saldo do período"
              value={cf ? fmtBRL(cf.netPeriod) : "—"}
              delta={netDelta}
              comparisonLabel="fórmula: entradas − saídas"
              status={cf ? (cf.netPeriod >= 0 ? "good" : "critical") : "neutral"}
              loading={isLoading}
            />
          </div>
          <MemoryLink cardKey="saldo-bancario">
            <KPIStatCard
              label="Saldo bancário"
              value={cf ? fmtBRL(cf.cashBalance) : "—"}
              comparisonLabel="posição atual"
              status="neutral"
              loading={isLoading}
            />
          </MemoryLink>
        </div>
      </div>

      {/* ── Posição em aberto / vencidos ─────────────────────────────────────── */}
      <div>
        <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest mb-3">Posição em aberto</p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <MemoryLink cardKey="a-receber-em-aberto">
            <KPIStatCard
              label="A receber em aberto"
              value={cf ? fmtBRL(cf.arOpen) : "—"}
              comparisonLabel="contas a receber"
              status="neutral"
              loading={isLoading}
            />
          </MemoryLink>
          <MemoryLink cardKey="a-pagar-em-aberto">
            <KPIStatCard
              label="A pagar em aberto"
              value={cf ? fmtBRL(cf.apOpen) : "—"}
              comparisonLabel="contas a pagar ⚠️"
              status="neutral"
              loading={isLoading}
            />
          </MemoryLink>
          <MemoryLink cardKey="inadimplencia-total">
            <KPIStatCard
              label="Vencidos a receber"
              value={cf ? fmtBRL(cf.arOverdue) : "—"}
              comparisonLabel="inadimplência"
              status={cf ? (cf.arOverdue > 0 ? "warning" : "good") : "neutral"}
              loading={isLoading}
            />
          </MemoryLink>
          <MemoryLink cardKey="pagamentos-atrasados">
            <KPIStatCard
              label="Vencidos a pagar"
              value={cf ? fmtBRL(cf.apOverdue) : "—"}
              comparisonLabel="atrasados ⚠️"
              status={cf ? (cf.apOverdue > 0 ? "warning" : "good") : "neutral"}
              loading={isLoading}
            />
          </MemoryLink>
        </div>
      </div>

      {/* ── Próximos 30 dias (posição projetada) ─────────────────────────────── */}
      <div>
        <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest mb-3">Próximos 30 dias</p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <MemoryLink cardKey="recebimentos-previstos-30d">
            <KPIStatCard
              label="A receber em 30d"
              value={cf ? fmtBRL(cf.ar30d) : "—"}
              comparisonLabel="vencimentos futuros"
              status="neutral"
              loading={isLoading}
            />
          </MemoryLink>
          <MemoryLink cardKey="pagamentos-previstos-30d">
            <KPIStatCard
              label="A pagar em 30d"
              value={cf ? fmtBRL(cf.ap30d) : "—"}
              comparisonLabel="vencimentos futuros ⚠️"
              status="neutral"
              loading={isLoading}
            />
          </MemoryLink>
          <MemoryLink cardKey="fluxo-projetado-30d">
            <KPIStatCard
              label="Gap 30 dias"
              value={cf ? fmtBRL(cf.gap30d) : "—"}
              comparisonLabel="a receber − a pagar"
              status={cf ? (cf.gap30d >= 0 ? "good" : "critical") : "neutral"}
              loading={isLoading}
            />
          </MemoryLink>
          <div className="cursor-default" title="Valor derivado: Saldo bancário + Gap 30 dias (previsão contratual — só títulos registrados)">
            <KPIStatCard
              label="Posição projetada 30d"
              value={cf ? fmtBRL(cf.positionProjected30d) : "—"}
              comparisonLabel="fórmula: saldo bancário + gap 30d"
              status={cf ? (cf.positionProjected30d >= 0 ? "good" : "critical") : "neutral"}
              loading={isLoading}
            />
          </div>
        </div>
      </div>

      {/* ── Saldo projetado dia a dia ────────────────────────────────────────── */}
      {(cf?.projectionDaily ?? []).length > 0 && (
        <ChartCard
          title="Saldo projetado dia a dia"
          subtitle="Projeção diária da origem (contas a receber/pagar cadastradas) — próximos 60 dias"
          height={280}
          loading={isLoading}
          isEmpty={false}
        >
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={(cf?.projectionDaily ?? []).map((p) => ({
              date: dayLabel(p.date), rawDate: p.date, Receber: p.receivable, Pagar: p.payable, "Saldo projetado": p.balance,
            }))} margin={{ top: 5, right: 5, left: -10, bottom: 0 }}
              onClick={(st) => {
                const raw = (st?.activePayload?.[0]?.payload as { rawDate?: string } | undefined)?.rawDate;
                if (raw) setProjDay(raw);
              }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#9ca3af" }} tickLine={false} axisLine={false} />
              <YAxis tickFormatter={axisK} tick={{ fontSize: 11, fill: "#9ca3af" }} tickLine={false} axisLine={false} />
              <Tooltip
                formatter={(v: number, n: string) => [fmtBRL(v), n]}
                contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e7ecef" }}
              />
              <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
              <ReferenceLine y={0} stroke="#DC2626" strokeDasharray="3 3" />
              <Bar dataKey="Receber" fill="var(--brand)" radius={[2, 2, 0, 0]} />
              <Bar dataKey="Pagar" fill="#F59E0B" radius={[2, 2, 0, 0]} />
              <Line type="monotone" dataKey="Saldo projetado" stroke="#6366f1" strokeWidth={2} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </ChartCard>
      )}

      {/* Drill do dia clicado na projeção: títulos a receber/pagar daquele dia */}
      {projDay && (
        <div className="card px-5 py-4 flex flex-wrap items-center gap-3 border-l-4 border-tm-500">
          <p className="text-sm text-gray-700">
            <span className="font-semibold">Dia {dayLabel(projDay)}</span> — abrir os títulos com vencimento neste dia:
          </p>
          <button
            className="btn-secondary py-1.5 text-xs"
            onClick={() => { const w = dayWindow(projDay); navigate(`/financeiro/memoria/recebimentos-previstos-30d?periodFrom=${encodeURIComponent(w.from)}&periodTo=${encodeURIComponent(w.to)}`); }}
          >
            A receber do dia
          </button>
          <button
            className="btn-secondary py-1.5 text-xs"
            onClick={() => { const w = dayWindow(projDay); navigate(`/financeiro/memoria/pagamentos-previstos-30d?periodFrom=${encodeURIComponent(w.from)}&periodTo=${encodeURIComponent(w.to)}`); }}
          >
            A pagar do dia
          </button>
          <button className="text-xs text-gray-400 hover:text-gray-600 ml-auto" onClick={() => setProjDay(null)}>fechar ✕</button>
        </div>
      )}

      {/* ── Evolução diária ─────────────────────────────────────────────────── */}
      <ChartCard
        title="Entradas × Saídas × Saldo"
        subtitle="Evolução diária dentro do período selecionado"
        height={300}
        loading={isLoading}
        isEmpty={dailyData.length === 0}
        emptyMessage="Sem movimentações no período."
      >
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={dailyData} margin={{ top: 5, right: 5, left: -10, bottom: 0 }}
            onClick={(st) => {
              const raw = (st?.activePayload?.[0]?.payload as { rawDate?: string } | undefined)?.rawDate;
              if (raw) setRealDay(raw);
            }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#9ca3af" }} tickLine={false} axisLine={false} />
            <YAxis tickFormatter={axisK} tick={{ fontSize: 11, fill: "#9ca3af" }} tickLine={false} axisLine={false} />
            <Tooltip
              formatter={(v: number, n: string) => [fmtBRL(v), n]}
              contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e7ecef" }}
            />
            <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
            <ReferenceLine y={0} stroke="#DC2626" strokeDasharray="3 3" />
            <Bar dataKey="Entradas" fill="var(--brand)" radius={[2, 2, 0, 0]} />
            <Bar dataKey="Saídas"   fill="#F59E0B" radius={[2, 2, 0, 0]} />
            <Line type="monotone" dataKey="Saldo" stroke="#6366f1" strokeWidth={2} dot={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* Drill do dia clicado no realizado: recebimentos/pagamentos daquele dia */}
      {realDay && (
        <div className="card px-5 py-4 flex flex-wrap items-center gap-3 border-l-4 border-tm-500">
          <p className="text-sm text-gray-700">
            <span className="font-semibold">Dia {dayLabel(realDay)}</span> — abrir os movimentos realizados neste dia:
          </p>
          <button
            className="btn-secondary py-1.5 text-xs"
            onClick={() => { const w = dayWindow(realDay); navigate(`/financeiro/memoria/recebido-no-periodo?periodFrom=${encodeURIComponent(w.from)}&periodTo=${encodeURIComponent(w.to)}`); }}
          >
            Recebimentos do dia
          </button>
          <button
            className="btn-secondary py-1.5 text-xs"
            onClick={() => { const w = dayWindow(realDay); navigate(`/financeiro/memoria/pago-no-periodo?periodFrom=${encodeURIComponent(w.from)}&periodTo=${encodeURIComponent(w.to)}`); }}
          >
            Pagamentos do dia
          </button>
          <button className="text-xs text-gray-400 hover:text-gray-600 ml-auto" onClick={() => setRealDay(null)}>fechar ✕</button>
        </div>
      )}

      {/* ── Composição: top entradas / saídas ────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card overflow-hidden">
          <div className="px-5 py-4 border-b border-surface-border">
            <p className="text-sm font-semibold text-gray-900">Principais Entradas</p>
            <p className="text-xs text-gray-400 mt-0.5">Recebimentos por cliente no período</p>
          </div>
          <div className="divide-y divide-surface-border">
            {isLoading ? (
              <p className="px-5 py-4 text-sm text-gray-400">Carregando…</p>
            ) : (cf?.topInflows ?? []).length === 0 ? (
              <p className="px-5 py-4 text-sm text-gray-400">Sem recebimentos no período.</p>
            ) : (
              cf!.topInflows.map((s, i) => (
                <div key={i} className="px-5 py-3 flex items-center gap-3">
                  <span className="text-xs font-bold text-gray-400 w-5">{i + 1}</span>
                  <p className="flex-1 min-w-0 text-sm font-medium text-gray-800 truncate">{s.name}</p>
                  <p className="text-sm font-semibold text-tm-700">{s.formatted}</p>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="card overflow-hidden">
          <div className="px-5 py-4 border-b border-surface-border">
            <p className="text-sm font-semibold text-gray-900">Principais Saídas</p>
            <p className="text-xs text-gray-400 mt-0.5">Pagamentos por fornecedor no período ⚠️</p>
          </div>
          <div className="divide-y divide-surface-border">
            {isLoading ? (
              <p className="px-5 py-4 text-sm text-gray-400">Carregando…</p>
            ) : (cf?.topOutflows ?? []).length === 0 ? (
              <p className="px-5 py-4 text-sm text-gray-400">Sem pagamentos no período.</p>
            ) : (
              cf!.topOutflows.map((s, i) => (
                <div key={i} className="px-5 py-3 flex items-center gap-3">
                  <span className="text-xs font-bold text-gray-400 w-5">{i + 1}</span>
                  <p className="flex-1 min-w-0 text-sm font-medium text-gray-800 truncate">{s.name}</p>
                  <p className="text-sm font-semibold text-danger-600">{s.formatted}</p>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* ── Projeção mensal ──────────────────────────────────────────────────── */}
      <div className="card overflow-hidden">
        <div className="px-5 py-4 border-b border-surface-border">
          <p className="text-sm font-semibold text-gray-900">Projeção de Caixa — Próximos Períodos</p>
          <p className="text-xs text-gray-400 mt-0.5">Com base nas contas a receber e a pagar cadastradas</p>
        </div>
        <div className="divide-y divide-surface-border">
          {isLoading ? (
            <p className="px-5 py-4 text-sm text-gray-400">Carregando…</p>
          ) : futureRows.length === 0 ? (
            <p className="px-5 py-4 text-sm text-gray-400">Sem projeções cadastradas.</p>
          ) : (
            futureRows.map((p, i) => {
              const isNeg = p.balance < 0;
              return (
                <div key={i} className="px-5 py-4 flex items-center gap-6">
                  <div className="w-16 text-center flex-shrink-0">
                    <p className="text-sm font-bold text-gray-900">{monthLabel(p.period)}</p>
                    <p className="text-xs text-gray-400">{p.period.slice(0, 4)}</p>
                  </div>
                  <div className="flex-1 grid grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs text-gray-500">A receber</p>
                      <p className="text-sm font-medium text-tm-700">{fmtBRL(p.receivable)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">A pagar</p>
                      <p className="text-sm font-medium text-danger-600">{fmtBRL(p.payable)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Saldo</p>
                      <p className={`text-sm font-bold ${isNeg ? "text-danger-700" : "text-tm-700"}`}>
                        {isNeg ? `(${fmtBRL(Math.abs(p.balance))})` : fmtBRL(p.balance)}
                      </p>
                    </div>
                  </div>
                  {isNeg && <span className="badge-red flex-shrink-0">Risco</span>}
                </div>
              );
            })
          )}
        </div>
        {(cf?.warnings ?? []).length > 0 && (
          <div className="px-5 py-3 bg-warning-50 border-t border-warning-100">
            {cf!.warnings.map((w, i) => (
              <p key={i} className="text-xs text-warning-700 flex items-start gap-1.5">
                <IconInfo className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                {w}
              </p>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
